import { ModosDeAcao, OpcoesAvancadas } from '../../dominio/assistente.interface'
import type { OpcoesAvancadasDaSessao } from '../../servicos/assistantClient'

/**
 * Opções avançadas do Assistente: ligar e desligar as ações de TELA e as de
 * SISTEMA do MCP, conversa a conversa. É ferramenta de teste — quem decide se
 * o menu aparece é o agente, por variável de ambiente.
 *
 * A escolha é DA CONVERSA em que foi feita: sobrevive ao F5 e a reabrir o
 * dock nela, e toda conversa nova começa no padrão da sessão (os dois
 * ligados). Guardada por pessoa e empresa, ela vazava: um teste que desligou
 * a tela na conversa 49d03a15 (alpha-arquitetos, 15/10/2026) fez a "Nova
 * conversa" seguinte nascer sem ações de tela, com o menu dizendo "nesta
 * conversa".
 *
 * Este módulo é puro: lê e guarda a preferência da pessoa e decide o que o
 * turno leva. Quem guarda o estado é a store; quem desenha é
 * `OpcoesAvancadas.vue`.
 */

export type ModoDeAcao = keyof ModosDeAcao

/** Menu oculto e os dois modos ligados: o turno sai como saía antes do menu. */
export const OPCOES_AVANCADAS_PADRAO: OpcoesAvancadas = Object.freeze({
  exibir: false,
  acoesDeTela: true,
  acoesDeSistema: true
})

/** Armazenamento mínimo: o `localStorage` do navegador ou um dublê de teste. */
export interface ArmazenamentoDeOpcoes {
  getItem (chave: string): string | null
  setItem (chave: string, valor: string): void
}

/**
 * Uma chave por pessoa E empresa (trocar de empresa não herda o teste); a
 * conversa vai DENTRO do valor, e não na chave, para que guardar a escolha de
 * uma conversa nova descarte a da anterior em vez de acumular chaves.
 */
export function chaveDasOpcoesAvancadas (userId: string, companyId: string): string {
  return `assistentePontotel.opcoesAvancadas.${userId}.${companyId}`
}

function armazenamentoDoNavegador (): ArmazenamentoDeOpcoes | null {
  try {
    const armazenamento = (globalThis as { localStorage?: ArmazenamentoDeOpcoes }).localStorage
    return armazenamento && typeof armazenamento.getItem === 'function' ? armazenamento : null
  } catch {
    return null
  }
}

/**
 * O que a pessoa deixou salvo NESTA conversa, ou `null`. Qualquer coisa que
 * não seja o par de booleanos da mesma conversa — chave ausente, JSON
 * quebrado, outra conversa, o formato antigo sem conversa, armazenamento
 * bloqueado — vale como "nada salvo": quem decide então é o padrão da sessão.
 */
export function lerPreferenciaDeModos (
  userId: string,
  companyId: string,
  sessaoId: string | null | undefined,
  armazenamento: ArmazenamentoDeOpcoes | null = armazenamentoDoNavegador()
): ModosDeAcao | null {
  if (!armazenamento || !userId || !companyId || !sessaoId) return null
  try {
    const bruto = armazenamento.getItem(chaveDasOpcoesAvancadas(userId, companyId))
    if (!bruto) return null
    const lido = JSON.parse(bruto) as unknown
    if (!lido || typeof lido !== 'object' || Array.isArray(lido)) return null
    const { conversa, tela, sistema } = lido as Record<string, unknown>
    if (conversa !== sessaoId) return null
    if (typeof tela !== 'boolean' || typeof sistema !== 'boolean') return null
    return { tela, sistema }
  } catch {
    return null
  }
}

/**
 * Guardar é conveniência: falhar (aba anônima, cota) não muda a conversa.
 * Sem conversa não há o que guardar — a escolha não teria a quem pertencer.
 */
export function guardarPreferenciaDeModos (
  userId: string,
  companyId: string,
  sessaoId: string | null | undefined,
  modos: ModosDeAcao,
  armazenamento: ArmazenamentoDeOpcoes | null = armazenamentoDoNavegador()
): void {
  if (!armazenamento || !userId || !companyId || !sessaoId) return
  try {
    armazenamento.setItem(
      chaveDasOpcoesAvancadas(userId, companyId),
      JSON.stringify({ conversa: sessaoId, tela: modos.tela === true, sistema: modos.sistema === true })
    )
  } catch {
    // Sem armazenamento a escolha vale até o fim da sessão, e é só isso.
  }
}

/**
 * O estado do menu a partir da sessão e da preferência salva.
 *
 * Com o menu oculto a preferência NÃO vale: o agente ignora o que o front
 * mandar e força os dois modos, e um toggle desligado de um teste antigo não
 * pode tirar a leitura da tela de quem nem vê o menu.
 */
export function resolverOpcoesAvancadas (
  daSessao: OpcoesAvancadasDaSessao | null | undefined,
  preferencia: ModosDeAcao | null
): OpcoesAvancadas {
  if (!daSessao?.exibir) return { ...OPCOES_AVANCADAS_PADRAO }
  const modos = preferencia || daSessao.modosPadrao
  return { exibir: true, acoesDeTela: modos.tela !== false, acoesDeSistema: modos.sistema !== false }
}

/** Os modos como o turno os leva, ou `null` com o menu oculto (corpo de sempre). */
export function modosDeAcaoParaEnvio (opcoes: OpcoesAvancadas | null | undefined): ModosDeAcao | null {
  if (!opcoes?.exibir) return null
  return { tela: opcoes.acoesDeTela === true, sistema: opcoes.acoesDeSistema === true }
}

/**
 * Ações de tela desligadas: a tela da pessoa nem é colhida. O agente também
 * ignoraria o bloco, mas o que não precisa sair do navegador não sai.
 */
export function deveEnviarContextoDeTela (opcoes: OpcoesAvancadas | null | undefined): boolean {
  return !opcoes?.exibir || opcoes.acoesDeTela === true
}

/** Liga ou desliga um modo, sem mexer no outro nem na exibição. */
export function comModo (opcoes: OpcoesAvancadas, modo: ModoDeAcao, ligado: boolean): OpcoesAvancadas {
  return modo === 'tela'
    ? { ...opcoes, acoesDeTela: ligado === true }
    : { ...opcoes, acoesDeSistema: ligado === true }
}

/** Faixa horizontal de um retângulo na tela, em px. */
export interface FaixaHorizontal {
  left: number
  right: number
}

/**
 * Quanto o popover precisa andar na horizontal para caber no limite (o dock,
 * que recorta com `overflow: hidden`), com `margem` de folga nas bordas.
 * Positivo empurra para a direita. O popover nasce alinhado à direita do
 * botão, e o botão fica longe da borda direita do dock acoplado: sem esse
 * ajuste, os interruptores do lado esquerdo ficavam cortados.
 */
export function deslocamentoParaCaber (popover: FaixaHorizontal, limite: FaixaHorizontal, margem = 0): number {
  const minimo = limite.left + margem
  const maximo = limite.right - margem
  if (popover.right - popover.left > maximo - minimo) return minimo - popover.left
  if (popover.left < minimo) return minimo - popover.left
  if (popover.right > maximo) return maximo - popover.right
  return 0
}
